import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Loader2, X, ShieldCheck, Table2, History } from "lucide-react";
import PermissionManager from "@/components/access/PermissionManager";
import AccessMatrix from "@/components/access/AccessMatrix";

const SCOPES = ["Platform", "Tenant", "Company", "Department", "Team", "Self"];

export default function RolesPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // role being edited (permissions)
  const [editTab, setEditTab] = useState("permissions");
  const [history, setHistory] = useState({ loading: false, items: [] });
  const [showMatrix, setShowMatrix] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ roleCode: "", roleName: "", description: "", scope: "Tenant", level: 5 });
  const [saving, setSaving] = useState(false);

  const canEdit = hasPermission("ROLE.EDIT");
  const canCreate = hasPermission("ROLE.CREATE");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/v1/roles");
      setRows(Array.isArray(res.data.data) ? res.data.data : res.data.data.items || []);
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (role) => {
    setEditing(role);
    setEditTab("permissions");
    setHistory({ loading: false, items: [] });
  };

  const loadHistory = useCallback(async (roleId) => {
    setHistory({ loading: true, items: [] });
    try {
      const res = await api.get("/v1/audit", {
        params: { entityType: "ROLE", entityId: roleId, action: "ROLE_PERMISSION_UPDATE", size: 50 },
      });
      setHistory({ loading: false, items: res.data.data.items || [] });
    } catch {
      setHistory({ loading: false, items: [] });
    }
  }, []);

  useEffect(() => {
    if (editing && editTab === "history") loadHistory(editing.id);
  }, [editing, editTab, loadHistory]);

  const createRole = async () => {
    setSaving(true);
    try {
      await api.post("/v1/roles", { ...form, level: Number(form.level) || 5, permissionCodes: [] });
      toast.success("Role created");
      setCreating(false);
      setForm({ roleCode: "", roleName: "", description: "", scope: "Tenant", level: 5 });
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: "roleCode", header: "Code", render: (r) => <span className="font-mono text-xs font-semibold">{r.roleCode}</span> },
    { key: "roleName", header: "Name" },
    { key: "scope", header: "Scope", render: (r) => <Badge color="teal">{r.scope}</Badge> },
    { key: "level", header: "Level" },
    { key: "permissionCodes", header: "Permissions", render: (r) => (r.permissionCodes || []).length },
    { key: "isSystem", header: "System", render: (r) => (r.isSystem ? <Badge color="amber">System</Badge> : "—") },
    {
      key: "_actions", header: "",
      render: (r) => (
        <div className="flex items-center justify-end">
          {canEdit && (
            <button onClick={() => openEdit(r)} className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-muted" data-testid={`edit-role-${r.roleCode}`}>
              <Pencil className="h-3.5 w-3.5" /> Permissions
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="resource-role">
      <PageHeader
        title="Roles" subtitle="Enterprise RBAC roles & menu-driven permission assignment"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => setShowMatrix(true)} className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-semibold hover:bg-muted" data-testid="open-access-matrix-btn">
              <Table2 className="h-4 w-4" /> Access Matrix
            </button>
            {canCreate && (
              <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid="resource-create-btn">
                <Plus className="h-4 w-4" /> New Role
              </button>
            )}
          </div>
        }
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="role-table" />

      {/* Create role modal */}
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setCreating(false)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="role-create-dialog">
            <h3 className="mb-4 text-lg font-bold">Create Role</h3>
            <div className="space-y-3">
              {[["roleCode", "Role Code", "SALES_MANAGER"], ["roleName", "Role Name", ""], ["description", "Description", ""]].map(([k, label, ph]) => (
                <div key={k}>
                  <label className="mb-1 block text-sm font-medium">{label}</label>
                  <input value={form[k]} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} placeholder={ph} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`role-field-${k}`} />
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Scope</label>
                  <select value={form.scope} onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="role-field-scope">
                    {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Level</label>
                  <input type="number" value={form.level} onChange={(e) => setForm((p) => ({ ...p, level: e.target.value }))} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="role-field-level" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted" data-testid="role-create-cancel">Cancel</button>
              <button onClick={createRole} disabled={saving || !form.roleCode || !form.roleName} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60" data-testid="role-create-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit role / permission manager drawer */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={() => setEditing(null)}>
          <div className="flex h-full w-full max-w-2xl flex-col bg-card shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="role-edit-drawer">
            <div className="flex items-start justify-between border-b border-border p-5">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-bold text-foreground">
                  <ShieldCheck className="h-5 w-5 text-primary" /> Edit Role — {editing.roleName}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  <span className="font-mono">{editing.roleCode}</span> · {editing.scope} · Level {editing.level}
                </p>
              </div>
              <button onClick={() => setEditing(null)} className="rounded-md p-1.5 hover:bg-muted" data-testid="role-edit-close"><X className="h-5 w-5" /></button>
            </div>
            {/* Tabs */}
            <div className="flex gap-1 border-b border-border px-5">
              {[["permissions", "Permissions"], ["history", "History"]].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setEditTab(k)}
                  data-testid={`role-tab-${k}`}
                  className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold transition-colors ${
                    editTab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {label === "History" ? <span className="inline-flex items-center gap-1.5"><History className="h-3.5 w-3.5" />{label}</span> : label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {editTab === "permissions" ? (
                <>
                  <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">Menu &amp; Feature Permissions</h4>
                  <PermissionManager
                    roleId={editing.id}
                    roleCode={editing.roleCode}
                    isSuperAdmin={editing.roleCode === "SUPERADMIN"}
                    onSaved={load}
                    roles={rows}
                  />
                </>
              ) : (
                <div data-testid="role-history">
                  <h4 className="mb-3 text-sm font-bold uppercase tracking-wide text-muted-foreground">Permission Change History</h4>
                  {history.loading ? (
                    <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                  ) : history.items.length === 0 ? (
                    <p className="py-8 text-center text-sm text-muted-foreground" data-testid="role-history-empty">No permission changes recorded yet.</p>
                  ) : (
                    <ul className="space-y-3">
                      {history.items.map((h) => (
                        <li key={h.id} className="rounded-lg border border-border bg-background p-3 text-sm" data-testid="role-history-item">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-foreground">{h.actorEmail || h.actorUserId || "system"}</span>
                            <span className="text-xs text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {(h.after?.granted || []).map((c) => (
                              <span key={`g-${c}`} className="rounded-md bg-emerald-50 px-2 py-0.5 font-mono text-[10px] text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">+ {c}</span>
                            ))}
                            {(h.after?.revoked || []).map((c) => (
                              <span key={`r-${c}`} className="rounded-md bg-red-50 px-2 py-0.5 font-mono text-[10px] text-red-700 dark:bg-red-950/30 dark:text-red-400">− {c}</span>
                            ))}
                            {!(h.after?.granted || []).length && !(h.after?.revoked || []).length && (
                              <span className="text-xs text-muted-foreground">No menu changes</span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showMatrix && <AccessMatrix onClose={() => setShowMatrix(false)} />}
    </div>
  );
}
